import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { 
  AlertTriangle, 
  CreditCard, 
  CalendarClock, 
  FileWarning,
  ChevronRight,
  AlertCircle
} from "lucide-react";

export type ActionItemPriority = "high" | "medium" | "low";
export type ActionItemType = "overdue" | "nach_pending" | "maturing" | "mra_expiring" | "charge_filing" | "tax_invoice" | "insurance_pending" | "insurance_expiring";

export interface ActionItem {
  id: string;
  type: ActionItemType;
  title: string;
  description: string;
  priority: ActionItemPriority;
  count?: number;
  link?: string;
}

interface ActionItemsCardProps {
  items: ActionItem[];
  onItemClick?: (item: ActionItem) => void;
  maxItems?: number;
  loading?: boolean;
}

const priorityStyles = {
  high: {
    bg: "bg-destructive/5",
    border: "border-l-destructive",
    badge: "bg-destructive/10 text-destructive",
  },
  medium: {
    bg: "bg-chart-gold/5",
    border: "border-l-chart-gold",
    badge: "bg-chart-gold/15 text-foreground",
  },
  low: {
    bg: "bg-primary/5",
    border: "border-l-primary",
    badge: "bg-primary/10 text-primary",
  },
};

const typeIcons = {
  overdue: AlertTriangle,
  nach_pending: CreditCard,
  maturing: CalendarClock,
  mra_expiring: FileWarning,
  charge_filing: FileWarning,
  tax_invoice: FileWarning,
  insurance_pending: AlertTriangle,
  insurance_expiring: CalendarClock,
};

export const ActionItemsCard = ({ items, onItemClick, maxItems = 5, loading = false }: ActionItemsCardProps) => {
  const [showAll, setShowAll] = useState(false);
  const displayItems = showAll ? items : items.slice(0, maxItems);
  const hasMore = items.length > maxItems;

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">Action Items</CardTitle>
          {!loading && items.length > 0 && (
            <Badge variant="secondary" className="font-normal">
              {items.reduce((sum, item) => sum + (item.count ?? 1), 0)} pending
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-14 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <AlertCircle className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              No action items. All caught up!
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayItems.map((item) => {
              const IconComponent = typeIcons[item.type];
              const styles = priorityStyles[item.priority];

              return (
                <Button
                  variant="ghost"
                  key={item.id}
                  className={cn(
                    "flex h-auto w-full cursor-pointer items-center justify-start gap-3 whitespace-normal rounded-md border-l-4 p-3 text-left",
                    styles.bg,
                    styles.border
                  )}
                  onClick={() => onItemClick?.(item)}
                >
                  <div className="h-8 w-8 rounded-lg bg-background flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-4 w-4 text-muted-foreground" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{item.title}</span>
                      {/* {item.count !== undefined && (
                        <Badge variant="secondary" className="text-xs">
                          {item.count}
                        </Badge>
                      )} */}
                    </div>
                    <p className="text-xs text-muted-foreground whitespace-normal">
                      {item.description}
                    </p>
                  </div>
                  {item.count !== undefined && (
                  <Badge variant="default" className="text-xs">
                  {item.count}
                </Badge>
                  )}

                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </Button>
              );
            })}

            {hasMore && (
              <Button
                variant="ghost"
                className="w-full cursor-pointer text-sm"
                size="sm"
                onClick={() => setShowAll((value) => !value)}
              >
                {showAll ? "Show less" : `View all ${items.length} items`}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActionItemsCard;
